import type {ReactNode} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {HelpIcon} from '@/components/help-icon'
import {BarraEscalera, type Etapa, type EstadoEtapa} from '@/components/bateria/barra-escalera'
import {ValidacionResultado} from '@/components/bateria/validacion-resultado'
import {
  useDownloadZipFormas,
  useEntregarEvaluacion,
  useStartZipFormas,
  useValidarLote,
  type BateriaEvaluacion,
  type BateriaEvaluacionFase,
  type BateriaPaquete,
  type IngestaJob,
  type ResumenRevision,
} from '@/hooks/bateria/use-bateria'
import {faltaDelLoteAgregada, LOTE_SIN_AVANCE_MS, ORDEN_FASES, type LotesResumen} from '@/lib/bateria'
import {contarDejadas} from '@/lib/cola-dejadas'

export type AnclaGestion = 'lote-cuadernillos' | 'lote-escaneos'

type Props = {
  businessId: string
  evaluacionId: string
  evaluacion: BateriaEvaluacion
  paquetes: BateriaPaquete[]
  jobs: IngestaJob[]
  lotes: LotesResumen
  resumen: ResumenRevision | undefined
  onAgregarPaquetes: () => void
  onIrAGestion: (ancla: AnclaGestion, focoTestId?: string) => void
  onIrAlInforme: () => void
}

type Primaria = {descripcion: string; acciones: ReactNode}

/** El estado del lote se lee acá y se obra en las pestañas: sin controles de archivo ni de ciclo de vida. */
export function BarraEstado({
  businessId,
  evaluacionId,
  evaluacion,
  paquetes,
  jobs,
  lotes,
  resumen,
  onAgregarPaquetes,
  onIrAGestion,
  onIrAlInforme,
}: Props) {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const generarZip = useStartZipFormas(businessId, evaluacionId)
  const descargarZip = useDownloadZipFormas(businessId, evaluacionId)
  const validar = useValidarLote(businessId, evaluacionId)
  const entregar = useEntregarEvaluacion(businessId, evaluacionId)

  const lotePath = `/admin/bateria/empresas/${businessId}/evaluaciones/${evaluacionId}`
  const revisionPath = `${lotePath}/revision`
  const estado = evaluacion.estado
  // Cerrada y entregada ya no tienen fase propia: la escalera se pinta completa.
  const faseActual: BateriaEvaluacionFase = estado === 'abierta' ? evaluacion.fase : 'informe'

  const personas = paquetes.length
  const validados = paquetes.filter((p) => p.estado === 'validado').length
  const impresos = personas - evaluacion.paquetes_sin_imprimir.length
  // Las dos cifras salen de `revision/resumen`: `pendientes` ya incluye las hojas por ubicar.
  const secciones = resumen?.por_seccion ?? []
  const sinUbicar = secciones.find((s) => s.clave === 'ubicar')?.pendientes ?? 0
  const dudas = secciones.filter((s) => s.clave !== 'ubicar').reduce((total, s) => total + s.pendientes, 0)
  const dejadas = contarDejadas(businessId, evaluacionId)

  function conteoCuadernillos(): string | null {
    if (evaluacion.zip_formas_status === 'listo')
      return t('admin.bateria.barra.conteo.zipListo', {recibidos: evaluacion.cuadernillos_recibidos, total: personas})
    if (impresos > 0) return t('admin.bateria.barra.conteo.impresos', {impresos, total: personas})
    return t('admin.bateria.barra.conteo.sinGenerar')
  }

  function conteoInforme(): string | null {
    if (evaluacion.reporte_status === null) return null
    if (evaluacion.reporte_status === 'listo')
      return evaluacion.reporte_desactualizado
        ? t('admin.bateria.barra.conteo.informeDesactualizado')
        : t('admin.bateria.barra.conteo.informeGenerado')
    if (evaluacion.reporte_status === 'error') return t('admin.bateria.barra.conteo.informeSinGenerar')
    return t('admin.bateria.barra.conteo.informeGenerando')
  }

  const conteos: Record<BateriaEvaluacionFase, string | null> = {
    roster:
      personas === 0
        ? t('admin.bateria.barra.conteo.sinPersonas')
        : t('admin.bateria.barra.conteo.personas', {count: personas}),
    campo: conteoCuadernillos(),
    lectura:
      lotes.paginasDelArchivo === 0
        ? null
        : t('admin.bateria.barra.conteo.paginas', {leidas: lotes.leidas, total: lotes.paginasDelArchivo}),
    revision:
      dudas + sinUbicar === 0
        ? resumen
          ? t('admin.bateria.barra.conteo.sinPendientes')
          : null
        : t('admin.bateria.barra.conteo.revision', {dudas, sinUbicar}),
    listo_para_informe: personas === 0 ? null : t('admin.bateria.barra.conteo.validadas', {validados, total: personas}),
    informe: conteoInforme(),
  }

  // Solo hechos que el servidor ya publicó, nunca una sospecha de la pantalla.
  const bloqueos: Partial<Record<BateriaEvaluacionFase, string>> = {}
  if (evaluacion.zip_formas_status === 'error') bloqueos.campo = t('admin.bateria.barra.bloqueo.zip')
  if (lotes.detenido) bloqueos.lectura = t('admin.bateria.barra.bloqueo.lectura')
  if (evaluacion.reporte_status === 'error') bloqueos.informe = t('admin.bateria.barra.bloqueo.informe')

  const puertas: Partial<Record<BateriaEvaluacionFase, () => void>> = {
    campo: () => onIrAGestion('lote-cuadernillos'),
    lectura: () => onIrAGestion('lote-escaneos'),
    revision: () => navigate(revisionPath),
  }

  const indiceActual = ORDEN_FASES.indexOf(faseActual)
  const etapas: Etapa[] = ORDEN_FASES.map((fase, i) => {
    const bloqueo = bloqueos[fase]
    const posicion: EstadoEtapa = i < indiceActual ? 'hecha' : i === indiceActual ? 'actual' : 'futura'
    return {fase, estado: bloqueo ? 'bloqueada' : posicion, conteo: conteos[fase], bloqueo, onIr: puertas[fase]}
  })

  function verInforme(testId: string) {
    return (
      <Button variant="outline" data-testid={testId} onClick={onIrAlInforme}>
        {t('admin.bateria.siguientePaso.verInforme')}
      </Button>
    )
  }

  function primariaFinal(): Primaria {
    if (estado === 'entregada') {
      return {descripcion: t('admin.bateria.siguientePaso.entregada'), acciones: verInforme('siguiente-paso-primaria')}
    }
    return {
      descripcion: t('admin.bateria.siguientePaso.cerrada'),
      acciones: (
        <>
          {verInforme('siguiente-paso-ver-informe')}
          <Button data-testid="siguiente-paso-primaria" disabled={entregar.isPending} onClick={() => entregar.mutate()}>
            {t('admin.bateria.lifecycle.entregar')}
          </Button>
        </>
      ),
    }
  }

  /** Si el ZIP no está listo, lo manda a generar y lleva a su tarjeta. */
  function descargarCuadernillos() {
    if (evaluacion.zip_formas_status === 'listo') {
      descargarZip.mutate()
      return
    }
    generarZip.mutate([])
    onIrAGestion('lote-cuadernillos')
  }

  function primariaDeRoster(): Primaria {
    if (personas === 0) {
      return {
        descripcion: t('admin.bateria.siguientePaso.rosterVacio'),
        acciones: (
          <Button data-testid="siguiente-paso-primaria" onClick={onAgregarPaquetes}>
            {t('admin.bateria.detail.agregar')}
          </Button>
        ),
      }
    }
    return {
      descripcion: t('admin.bateria.siguientePaso.rosterHint'),
      acciones: (
        <Button
          data-testid="siguiente-paso-primaria"
          disabled={generarZip.isPending || descargarZip.isPending}
          onClick={descargarCuadernillos}>
          {t('admin.bateria.siguientePaso.descargarCuadernillos', {count: personas})}
        </Button>
      ),
    }
  }

  function primariaDeRevision(): Primaria {
    if (sinUbicar > 0) {
      return {
        descripcion: t('admin.bateria.siguientePaso.revisionUbicarPrimero'),
        acciones: (
          <Button data-testid="siguiente-paso-primaria" onClick={() => navigate(`${revisionPath}?ir_a=ubicar`)}>
            {t('admin.bateria.siguientePaso.ubicarHojas', {count: sinUbicar})}
          </Button>
        ),
      }
    }
    return {
      descripcion: t('admin.bateria.siguientePaso.revisionDudas'),
      acciones: (
        <Button data-testid="siguiente-paso-primaria" onClick={() => navigate(`${revisionPath}?revisar=pendientes`)}>
          {t('admin.bateria.siguientePaso.revisarDudas', {count: dudas})}
        </Button>
      ),
    }
  }

  function primariaDeLectura(): Primaria {
    // Sin primaria mientras la lectura avanza: un botón junto a una barra en movimiento invita a interrumpirla.
    if (!lotes.detenido) return {descripcion: t('admin.bateria.siguientePaso.lectura'), acciones: null}
    return {
      descripcion: t('admin.bateria.barra.bloqueo.lectura'),
      acciones: (
        <Button data-testid="siguiente-paso-primaria" onClick={() => onIrAGestion('lote-escaneos')}>
          {t('admin.bateria.barra.primaria.verTandas')}
        </Button>
      ),
    }
  }

  function primaria(): Primaria {
    if (estado !== 'abierta') return primariaFinal()
    switch (evaluacion.fase) {
      case 'roster':
        return primariaDeRoster()
      case 'campo':
        return {
          descripcion: t('admin.bateria.siguientePaso.campoSinZip'),
          acciones: (
            <Button
              data-testid="siguiente-paso-primaria"
              onClick={() => onIrAGestion('lote-escaneos', 'escaneos-elegir-archivos')}>
              {t('admin.bateria.siguientePaso.subirEscaneos')}
            </Button>
          ),
        }
      case 'lectura':
        return primariaDeLectura()
      case 'revision':
        return primariaDeRevision()
      case 'listo_para_informe':
        return {
          descripcion: t('admin.bateria.siguientePaso.listoParaInforme'),
          acciones: (
            <Button
              data-testid="siguiente-paso-primaria"
              disabled={validar.isPending}
              onClick={() => validar.mutate(undefined)}>
              {t('admin.bateria.siguientePaso.validarLote', {count: personas - validados})}
            </Button>
          ),
        }
      case 'informe':
        return {descripcion: t('admin.bateria.siguientePaso.informe'), acciones: verInforme('siguiente-paso-primaria')}
    }
  }

  const {descripcion, acciones} = primaria()
  // Sin chips en cero: enseñan a no mirar la fila.
  const chips = [
    {
      key: 'dudas',
      n: dudas,
      to: `${revisionPath}?revisar=pendientes`,
      texto: t('admin.bateria.barra.chip.dudas', {count: dudas}),
    },
    {
      key: 'sin-ubicar',
      n: sinUbicar,
      to: `${revisionPath}?ir_a=ubicar`,
      texto: t('admin.bateria.barra.chip.sinUbicar', {count: sinUbicar}),
    },
    {
      key: 'dejadas',
      n: dejadas,
      to: `${revisionPath}?revisar=dejadas`,
      texto: t('admin.bateria.barra.chip.dejadas', {count: dejadas}),
    },
  ].filter((chip) => chip.n > 0)

  return (
    <section className="surface space-y-3 px-5 py-4" data-testid="barra-estado">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <span className="font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
            {t('admin.bateria.barra.title')}
          </span>
          <p className="mt-1.5 text-sm text-foreground">{descripcion}</p>
          {validar.isError && (
            <p className="mt-1 text-xs text-destructive">{t('admin.bateria.siguientePaso.validarError')}</p>
          )}
          {entregar.isError && (
            <p className="mt-1 text-xs text-destructive">{t('admin.bateria.lifecycle.entregarError')}</p>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {/* Secundaria a propósito: la primaria es el único botón verde de la fila. */}
          <Button
            variant="outline"
            data-testid="barra-ver-cola"
            onClick={() => navigate(`${revisionPath}?revisar=pendientes`)}>
            {t('admin.bateria.barra.verCola')}
          </Button>
          {acciones}
        </div>
      </div>

      <BarraEscalera etapas={etapas} />

      {(faseActual === 'lectura' || lotes.corriendo) && (
        <ProgresoDeLectura jobs={jobs} lotes={lotes} onIrATandas={() => onIrAGestion('lote-escaneos')} />
      )}

      {(chips.length > 0 || lotes.conError > 0) && (
        <div className="flex flex-wrap items-center gap-2">
          {chips.map((chip) => (
            <Link
              key={chip.key}
              to={chip.to}
              data-testid={`barra-chip-${chip.key}`}
              className="tone-amber rounded-full px-2.5 py-1 text-xs hover:underline">
              {chip.texto}
            </Link>
          ))}
          {/* «Dejadas» es un marcador de este computador (`lib/cola-dejadas.ts`), no trabajo del lote. */}
          {dejadas > 0 && (
            <HelpIcon
              label={t('common.help.iconLabelFor', {campo: t('admin.bateria.barra.chip.dejadas', {count: dejadas})})}
              testId="barra-chip-dejadas-help">
              {t('admin.bateria.barra.chip.dejadasHelp')}
            </HelpIcon>
          )}
          {lotes.conError > 0 && (
            <button
              type="button"
              data-testid="barra-chip-con-error"
              onClick={() => onIrAGestion('lote-escaneos')}
              className="tone-red rounded-full px-2.5 py-1 text-xs hover:underline">
              {t('admin.bateria.barra.chip.conError', {count: lotes.conError})}
            </button>
          )}
        </div>
      )}

      {validar.data && (
        <ValidacionResultado
          resultado={validar.data}
          nombreDePaquete={(id) =>
            paquetes.find((p) => p.id === id)?.employee_name ?? t('admin.bateria.validacion.personaDesconocida')
          }
          arreglarHref={`${lotePath}?tab=personas`}
          accionFaltanDudas={
            <Button
              size="sm"
              variant="outline"
              data-testid="siguiente-paso-ir-a-dudas"
              onClick={() => navigate(revisionPath)}>
              {t('admin.bateria.validacion.seguirRevisando')}
            </Button>
          }
        />
      )}
    </section>
  )
}

function ProgresoDeLectura({
  jobs,
  lotes,
  onIrATandas,
}: {
  jobs: IngestaJob[]
  lotes: LotesResumen
  onIrATandas: () => void
}) {
  const {t} = useTranslation()
  const total = lotes.paginasDelArchivo
  const indeterminada = total === 0 && lotes.corriendo
  const anchoDe = (paginas: number) => (total > 0 ? (paginas / total) * 100 : 0)
  const resueltas = lotes.leidas + lotes.sinUbicar + lotes.conError
  const pct = total > 0 ? Math.round((resueltas / total) * 100) : 0
  const cuenta = {done: resueltas, total}
  const falta = faltaDelLoteAgregada(jobs, Date.now())
  const minutos = falta === null ? 0 : Math.round(falta / 60_000)
  const eta =
    falta === null
      ? t('admin.bateria.ingesta.etaCalculando', cuenta)
      : minutos < 1
        ? t('admin.bateria.ingesta.etaCasi', cuenta)
        : t('admin.bateria.ingesta.eta', {...cuenta, count: minutos})

  return (
    <div className="space-y-1.5">
      <div
        role="progressbar"
        aria-label={t('admin.bateria.barra.conteo.paginas', {leidas: lotes.leidas, total})}
        aria-valuenow={indeterminada ? undefined : pct}
        aria-valuemin={0}
        aria-valuemax={100}
        data-testid="barra-lectura-barra"
        data-indeterminate={indeterminada || undefined}
        className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
        {indeterminada ? (
          <span className="block h-full w-1/3 animate-pulse rounded-full bg-primary" />
        ) : (
          <span className="flex h-full w-full">
            <span className="h-full bg-primary transition-all" style={{width: `${anchoDe(lotes.leidas)}%`}} />
            <span className="swatch-amber h-full transition-all" style={{width: `${anchoDe(lotes.sinUbicar)}%`}} />
            <span className="swatch-red h-full transition-all" style={{width: `${anchoDe(lotes.conError)}%`}} />
          </span>
        )}
      </div>
      {lotes.detenido ? (
        <div className="notice-amber rounded-lg px-4 py-3" data-testid="barra-lectura-detenido">
          <p className="text-sm">
            {t('admin.bateria.ingesta.detenido', {count: Math.floor(LOTE_SIN_AVANCE_MS / 60_000)})}
          </p>
          <p className="mt-0.5 text-xs opacity-80">
            {lotes.porProcesar > 0
              ? t('admin.bateria.ingesta.detenidoAyuda', {count: lotes.porProcesar})
              : t('admin.bateria.ingesta.detenidoAyudaSinTotal')}
          </p>
          <button
            type="button"
            data-testid="barra-lectura-detenido-ir"
            onClick={onIrATandas}
            className="mt-1.5 font-mono text-[11px] tracking-wider uppercase underline underline-offset-2 outline-none focus-visible:ring-2 focus-visible:ring-ring/50">
            {t('admin.bateria.ingesta.detenidoIrEscaneos')} →
          </button>
        </div>
      ) : (
        <>
          <p className="text-xs text-muted-foreground" data-testid="barra-lectura-eta">
            {eta}
          </p>
          {lotes.corriendo && (
            <p className="text-xs text-muted-foreground" data-testid="barra-lectura-puede-cerrar">
              {t('admin.bateria.ingesta.puedeCerrar')}
            </p>
          )}
        </>
      )}
    </div>
  )
}
